var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import TreeView from './TreeView';
import { SkeletonLoader } from '@widget/loaders';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { customCollisionDetection } from './treeview-drag-utils';
import { findTreeNodeByIdSync, processAllTreeNodes, calculateDropPosition, parsePrefixedId } from './treeview-utils';
import './TreeViewCompare.scss';
export var Tree;
(function (Tree) {
    Tree["Left"] = "left";
    Tree["Right"] = "right";
})(Tree || (Tree = {}));
/**
 * @export
 *
 * TreeViewCompare component provides drag-and-drop functionality between two TreeView components
 *
 * ## How Cross-Tree Drag Works
 *
 * 1. **Single DndContext**: Wraps both trees for unified collision detection
 * 2. **Drag State Coordination**:
 *    - `isDragging`: Global state (true when ANY drag is happening)
 *    - `isDragSource`: Tree-specific state (identifies which tree is being dragged from)
 * 3. **Drop Target Highlighting**: Target tree receives global `isDragging=true`, enabling
 *    visual feedback even though it's not the drag source
 * 4. **Validation**: Optional `validateDrop` function for custom drop rules
 *
 * ## Usage Pattern
 *
 * - Set `dragDirection` to control allowed drag operations ('left-to-right', 'right-to-left', 'both')
 * - Implement `onCrossDrop` to handle data updates when nodes are moved between trees
 * - Use `renderLeftLabel`/`renderRightLabel` for tree-specific node rendering
 *
 * @example
 * ```tsx
 * <TreeViewCompare
 *   leftItems={currentNavigation}
 *   rightItems={configuredNavigation}
 *   leftTitle="Current Navigation"
 *   rightTitle="Available Items"
 *   dragDirection="right-to-left"
 *   onCrossDrop={(source, target, sourceTree, targetTree) => {
 *     console.log('Dropped', source.label, 'onto', target.label);
 *     // Handle the drop logic
 *   }}
 *   renderRightLabel={(node) => (
 *     <span style={{ color: node.isNew ? 'green' : 'inherit' }}>
 *       {node.label}
 *     </span>
 *   )}
 * />
 * ```
 */
const TreeViewCompare = forwardRef(({ leftItems, rightItems, leftTitle, rightTitle, leftLoading = false, rightLoading = false, dragDirection = 'both', onDragStart, onDragEnd, renderLeftLabel, renderRightLabel, renderLeftDetails, renderRightDetails, expandAllNodes = false, showPath = true, treeStyles, showHeaders = true, validateDrop, className, style, maxDepth, enableSearch, onSearch, enableAdd, addButtonLabel, onAdd, enableEdit, onEdit, enableDelete, onDelete, enableReordering, onReorder, enableInternalHierarchyChange, onInternalHierarchyChange, multiSelect, leftSelected, rightSelected, onSelect, showActionButtonOnlyOnHover, actionButtonsPosition, customActions, rightCustomActions, }, ref) => {
    var _a;
    const [dragState, setDragState] = useState({
        sourceNode: null,
        sourceTree: null,
        targetNode: null,
        targetTree: null,
        isDragging: false,
        dropPosition: null
    });
    const [mousePosition, setMousePosition] = useState(null);
    // Setup drag sensors
    const sensors = useSensors(useSensor(PointerSensor, {
        activationConstraint: {
            distance: 8,
        },
    }));
    const processedLeftNodesRef = useRef([]);
    const processedRightNodesRef = useRef([]);
    const leftTreeRef = useRef(null);
    const rightTreeRef = useRef(null);
    useEffect(() => {
        processAllTreeNodes([], leftItems, expandAllNodes)
            .then(res => processedLeftNodesRef.current = res || [])
            .catch(() => processedLeftNodesRef.current = []);
    }, [leftItems, expandAllNodes]);
    useEffect(() => {
        processAllTreeNodes([], rightItems, expandAllNodes)
            .then(res => processedRightNodesRef.current = res || [])
            .catch(() => processedRightNodesRef.current = []);
    }, [rightItems, expandAllNodes]);
    // Track mouse position during drag for accurate drop position calculation
    useEffect(() => {
        // Only track during drag
        if (!dragState.isDragging) {
            setMousePosition(null);
            return;
        }
        const handlePointerMove = (e) => {
            setMousePosition({ x: e.clientX, y: e.clientY });
        };
        document.addEventListener('pointermove', handlePointerMove);
        return () => {
            document.removeEventListener('pointermove', handlePointerMove);
        };
    }, [dragState.isDragging]);
    // Determine drag/drop permissions based on direction
    const { leftCanDrag, leftCanDrop, rightCanDrag, rightCanDrop } = useMemo(() => {
        switch (dragDirection) {
            case 'left-to-right':
                return {
                    leftCanDrag: true,
                    leftCanDrop: false,
                    rightCanDrag: false,
                    rightCanDrop: true,
                };
            case 'right-to-left':
                return {
                    leftCanDrag: false,
                    leftCanDrop: true,
                    rightCanDrag: true,
                    rightCanDrop: false,
                };
            case 'both':
                return {
                    leftCanDrag: true,
                    leftCanDrop: true,
                    rightCanDrag: true,
                    rightCanDrop: true,
                };
            default:
                return {
                    leftCanDrag: false,
                    leftCanDrop: false,
                    rightCanDrag: false,
                    rightCanDrop: false,
                };
        }
    }, [dragDirection]);
    // Determine which tree gets which features based on direction
    // left-to-right: right tree gets features
    // right-to-left: left tree gets features
    // both: both trees get features
    const leftFeatures = useMemo(() => {
        const hasFeatures = dragDirection === 'right-to-left' || dragDirection === 'both';
        return {
            enableSearch: hasFeatures && enableSearch,
            enableAdd: hasFeatures && enableAdd,
            enableEdit: hasFeatures && enableEdit,
            enableDelete: hasFeatures && enableDelete,
            enableReordering: hasFeatures && enableReordering,
            enableInternalHierarchyChange: hasFeatures && enableInternalHierarchyChange,
            multiSelect: hasFeatures && multiSelect,
        };
    }, [dragDirection, enableSearch, enableAdd, enableEdit, enableDelete, enableReordering, enableInternalHierarchyChange, multiSelect]);
    const rightFeatures = useMemo(() => {
        const hasFeatures = dragDirection === 'left-to-right' || dragDirection === 'both';
        return {
            enableSearch: hasFeatures && enableSearch,
            enableAdd: hasFeatures && enableAdd,
            enableEdit: hasFeatures && enableEdit,
            enableDelete: hasFeatures && enableDelete,
            enableReordering: hasFeatures && enableReordering,
            enableInternalHierarchyChange: hasFeatures && enableInternalHierarchyChange,
            multiSelect: hasFeatures && multiSelect,
        };
    }, [dragDirection, enableSearch, enableAdd, enableEdit, enableDelete, enableReordering, enableInternalHierarchyChange, multiSelect]);
    const handleSearch = (tree, query) => onSearch === null || onSearch === void 0 ? void 0 : onSearch(query, tree);
    const handleAdd = (tree, parentNode) => onAdd === null || onAdd === void 0 ? void 0 : onAdd(parentNode, tree);
    const handleEdit = (tree, node) => onEdit === null || onEdit === void 0 ? void 0 : onEdit(node, tree);
    const handleDelete = (tree, id) => onDelete === null || onDelete === void 0 ? void 0 : onDelete(id, tree);
    const handleSelect = (tree, selected) => onSelect === null || onSelect === void 0 ? void 0 : onSelect(selected, tree);
    const findNodeInTrees = (nodeId, tree) => {
        const nodes = (tree === Tree.Left) ? processedLeftNodesRef.current : processedRightNodesRef.current;
        const node = findTreeNodeByIdSync(nodes, nodeId);
        if (node)
            return node;
        return null;
    };
    const processTarget = (dropPosition, targetNode, targetTree) => {
        function processAsLastChild(parentNode) {
            const sibillings = (!parentNode)
                ? targetTree == Tree.Left ? processedLeftNodesRef.current : processedRightNodesRef.current
                : (parentNode === null || parentNode === void 0 ? void 0 : parentNode.children) || [];
            return {
                parentNode: targetNode,
                sourceNodeIndex: (sibillings === null || sibillings === void 0 ? void 0 : sibillings.length) + 1,
                sibillings: sibillings,
            };
        }
        // no drop position , so add the last child for targetnode 
        if (!dropPosition)
            return processAsLastChild(targetNode);
        switch (dropPosition) {
            case 'after':
            case 'before':
                const parent = (!!(targetNode === null || targetNode === void 0 ? void 0 : targetNode.parentId)) ? findNodeInTrees(targetNode === null || targetNode === void 0 ? void 0 : targetNode.parentId, targetTree) : null;
                const sibillings = (!parent)
                    ? targetTree == 'left' ? processedLeftNodesRef.current : processedRightNodesRef.current
                    : (parent === null || parent === void 0 ? void 0 : parent.children) || [];
                const targetIndex = sibillings.findIndex(s => s.id === (targetNode === null || targetNode === void 0 ? void 0 : targetNode.id));
                const index = dropPosition == 'after' ? targetIndex + 1 : targetIndex;
                return {
                    parentNode: parent || null,
                    sourceNodeIndex: index,
                    sibillings: sibillings
                };
            case 'child':
            default:
                return processAsLastChild(targetNode);
        }
    };
    const resetDragState = () => {
        setDragState({
            sourceNode: null,
            sourceTree: null,
            targetNode: null,
            targetTree: null,
            isDragging: false,
            dropPosition: null
        });
    };
    /**
     * Process a cross-tree drop operation - used by both handleDragEnd and triggerDrop
     */
    const processCrossTreeDrop = (sourceNodeId, targetNodeId, sourceTree, targetTree, dropPosition) => __awaiter(void 0, void 0, void 0, function* () {
        // Find source and target nodes in appropriate trees
        const sourceNode = findNodeInTrees(sourceNodeId, sourceTree);
        const targetNode = findNodeInTrees(targetNodeId, targetTree);
        if (!sourceNode || !targetNode) {
            throw new Error('Source or target node not found');
        }
        // Validate: trees must be different for cross-tree drop
        if (sourceTree === targetTree) {
            throw new Error('Cannot drop within the same tree. Use TreeView for same-tree operations.');
        }
        // Validate: target node is not disabled
        if (targetNode === null || targetNode === void 0 ? void 0 : targetNode.disableNode) {
            throw new Error('Cannot drop on disabled node');
        }
        // Custom validation if provided
        if (validateDrop && !validateDrop(sourceNode, targetNode, sourceTree, targetTree)) {
            throw new Error('Drop validation failed');
        }
        // Process target to get parent and index
        const { sourceNodeIndex, parentNode, sibillings } = processTarget(dropPosition, targetNode, targetTree);
        // Call the onDragEnd callback with processed data
        yield (onDragEnd === null || onDragEnd === void 0 ? void 0 : onDragEnd(sourceNode, parentNode, sourceNodeIndex, sibillings, sourceTree, targetTree));
    });
    // Expose triggerDrop method via ref
    useImperativeHandle(ref, () => ({
        triggerDrop: (sourceNodeId, targetNodeId, sourceTree, targetTree, dropPosition) => __awaiter(void 0, void 0, void 0, function* () {
            yield processCrossTreeDrop(sourceNodeId, targetNodeId, sourceTree, targetTree, dropPosition);
        }),
        expandLeft: () => { var _a; return (_a = leftTreeRef.current) === null || _a === void 0 ? void 0 : _a.expandAll(); },
        collapseLeft: () => { var _a; return (_a = leftTreeRef.current) === null || _a === void 0 ? void 0 : _a.collapseAll(); },
        expandRight: () => { var _a; return (_a = rightTreeRef.current) === null || _a === void 0 ? void 0 : _a.expandAll(); },
        collapseRight: () => { var _a; return (_a = rightTreeRef.current) === null || _a === void 0 ? void 0 : _a.collapseAll(); },
    }), [processedLeftNodesRef.current, processedRightNodesRef.current, validateDrop, onDragEnd]);
    // Drag handlers
    const handleDragStart = (event) => __awaiter(void 0, void 0, void 0, function* () {
        const { active } = event;
        const { tree, nodeId } = parsePrefixedId(active === null || active === void 0 ? void 0 : active.id);
        const sourceNode = findNodeInTrees(nodeId, tree);
        if (sourceNode) {
            setDragState({
                sourceNode: sourceNode,
                sourceTree: tree,
                isDragging: true,
                dropPosition: null
            });
            onDragStart === null || onDragStart === void 0 ? void 0 : onDragStart(sourceNode, tree);
        }
    });
    const handleDragOver = (event) => {
        var _a, _b, _c;
        const { tree, nodeId } = parsePrefixedId((_a = event === null || event === void 0 ? void 0 : event.over) === null || _a === void 0 ? void 0 : _a.id);
        if (!nodeId)
            return;
        const targetNode = findNodeInTrees(nodeId, tree);
        if (!targetNode)
            return;
        // Get target rectangle from @dnd-kit
        const overRect = (_b = event.over) === null || _b === void 0 ? void 0 : _b.rect;
        let dropPosition = null;
        if (overRect && mousePosition) {
            // When source and target share the same parent, always reorder (50/50 split, no child zone)
            const isSameParent = (dragState.sourceTree === tree) && (((_c = dragState.sourceNode) === null || _c === void 0 ? void 0 : _c.parentId) === (targetNode === null || targetNode === void 0 ? void 0 : targetNode.parentId));
            const isReordering = isSameParent;
            // Use real mouse Y position for accurate, scroll-aware calculation
            dropPosition = calculateDropPosition(mousePosition.y, overRect, isReordering);
        }
        setDragState(prev => (Object.assign(Object.assign({}, prev), { targetNode: targetNode, targetTree: tree, dropPosition: dropPosition })));
    };
    const handleDragMove = (event) => {
        var _a, _b, _c;
        let { tree: targetTree, nodeId: targetId } = parsePrefixedId((_a = event === null || event === void 0 ? void 0 : event.over) === null || _a === void 0 ? void 0 : _a.id);
        // If not over any node, clear drop position
        if (!targetId) {
            setDragState(prev => (Object.assign(Object.assign({}, prev), { dropPosition: null })));
            return;
        }
        const targetNode = findNodeInTrees(targetId, targetTree);
        if (!targetNode) {
            setDragState(prev => (Object.assign(Object.assign({}, prev), { dropPosition: null })));
            return;
        }
        // Get target rectangle from @dnd-kit
        const overRect = (_b = event.over) === null || _b === void 0 ? void 0 : _b.rect;
        let dropPosition = null;
        if (overRect && mousePosition) {
            // When source and target share the same parent, always reorder (50/50 split, no child zone)
            const isSameParent = (dragState.sourceTree === targetTree) && (((_c = dragState.sourceNode) === null || _c === void 0 ? void 0 : _c.parentId) === (targetNode === null || targetNode === void 0 ? void 0 : targetNode.parentId));
            const isReordering = isSameParent;
            // Use real mouse Y position for accurate, scroll-aware calculation
            dropPosition = calculateDropPosition(mousePosition.y, overRect, isReordering);
        }
        // Only update drop position (don't change targetNode/targetTree)
        setDragState(prev => (Object.assign(Object.assign({}, prev), { dropPosition: dropPosition })));
    };
    const handleDragEnd = () => __awaiter(void 0, void 0, void 0, function* () {
        const { sourceNode, sourceTree, targetNode, targetTree, dropPosition } = dragState;
        try {
            // Validate we have all required data
            if (!sourceNode || !targetNode || !sourceTree || !targetTree || !dropPosition) {
                return;
            }
            const { nodeId: sourcerId } = parsePrefixedId(sourceNode.id);
            const { nodeId: targetId } = parsePrefixedId(targetNode.id);
            // cannot drop to the same node
            if (sourcerId === targetId) {
                return;
            }
            // Handle cross-tree drops
            if (sourceTree !== targetTree) {
                yield processCrossTreeDrop(sourcerId, targetId, sourceTree, targetTree, dropPosition);
            }
            // Handle same-tree operations (reordering and hierarchy changes)
            else {
                const isSameParent = sourceNode.parentId === targetNode.parentId;
                // Same parent = reordering
                if (isSameParent && dropPosition != 'child' && onReorder) {
                    // Get siblings for reordering
                    const parentId = sourceNode.parentId;
                    let siblings;
                    if (parentId) {
                        const parent = findNodeInTrees(parentId, sourceTree);
                        siblings = (parent === null || parent === void 0 ? void 0 : parent.children) || [];
                    }
                    else {
                        siblings = sourceTree === Tree.Left ? processedLeftNodesRef.current : processedRightNodesRef.current;
                    }
                    // Find indices
                    const oldIndex = siblings.findIndex(n => n.id === sourceNode.id);
                    const movedNode = siblings[oldIndex];
                    const targetIndex = siblings.findIndex(n => n.id === targetNode.id);
                    // Calculate adjusted index based on position
                    // this is temporary index. keep calculation simple 
                    const adjustedIndex = dropPosition === 'after'
                        ? targetIndex + 1
                        : targetIndex;
                    // Reorder
                    const reordered = [...siblings];
                    // first append to new position
                    reordered.splice(adjustedIndex, 0, movedNode);
                    // then remove old node 
                    reordered.splice(adjustedIndex < oldIndex ? oldIndex + 1 : oldIndex, 1);
                    const newIndex = reordered.findIndex(n => n.id === sourceNode.id);
                    yield onReorder(reordered, sourceNode, newIndex, parentId, sourceTree);
                }
                // Different parent = hierarchy change
                else if ((!isSameParent || dropPosition == 'child') && onInternalHierarchyChange) {
                    const { sourceNodeIndex, parentNode, sibillings } = processTarget(dropPosition, targetNode, sourceTree);
                    yield onInternalHierarchyChange(sourceNode, sourceNodeIndex, sibillings, (parentNode === null || parentNode === void 0 ? void 0 : parentNode.id) || null, sourceTree);
                }
            }
        }
        catch (error) {
            console.error('Error during drag end:', error);
        }
        finally {
            resetDragState();
        }
    });
    // Determine if current over target is valid for cross-tree drop
    // This controls whether the target tree shows drop indicators
    const isCrossTreeDrop = (dragState === null || dragState === void 0 ? void 0 : dragState.sourceTree) && (dragState === null || dragState === void 0 ? void 0 : dragState.targetTree) && (dragState === null || dragState === void 0 ? void 0 : dragState.sourceTree) !== (dragState === null || dragState === void 0 ? void 0 : dragState.targetTree);
    const isDragAllowed = isCrossTreeDrop && (((dragState === null || dragState === void 0 ? void 0 : dragState.sourceTree) === Tree.Left && rightCanDrop) ||
        ((dragState === null || dragState === void 0 ? void 0 : dragState.sourceTree) === Tree.Right && leftCanDrop));
    const getExternalDragState = (tree) => {
        var _a, _b;
        // Only provide dropzoneNodeId if this tree is the target
        const isTargetTree = (dragState === null || dragState === void 0 ? void 0 : dragState.targetTree) === tree;
        const dropzoneNodeId = isTargetTree && ((_a = dragState === null || dragState === void 0 ? void 0 : dragState.targetNode) === null || _a === void 0 ? void 0 : _a.id)
            ? dragState.targetNode.id
            : null;
        return {
            isDragging: dragState === null || dragState === void 0 ? void 0 : dragState.isDragging,
            isDragSource: (dragState === null || dragState === void 0 ? void 0 : dragState.sourceTree) === tree,
            draggedNodeId: (_b = dragState === null || dragState === void 0 ? void 0 : dragState.sourceNode) === null || _b === void 0 ? void 0 : _b.id,
            dropzoneNodeId: dropzoneNodeId,
            dropPosition: dragState === null || dragState === void 0 ? void 0 : dragState.dropPosition
        };
    };
    return (_jsx(DndContext, { sensors: sensors, collisionDetection: customCollisionDetection, onDragStart: handleDragStart, onDragOver: handleDragOver, onDragMove: handleDragMove, onDragEnd: handleDragEnd, children: _jsxs("div", { className: `uxpcore_treeviewcompare ${className || ''}`, style: style, children: [_jsxs("div", { className: "uxpcore_treeviewcompare__panel uxpcore_treeviewcompare__panel--left", "data-tree-side": "left", children: [showHeaders && (_jsx("div", { className: "uxpcore_treeviewcompare__header", children: leftLoading ? (_jsx(SkeletonLoader, { width: '8rem', height: '1rem' })) : (leftTitle || 'Left Tree') })), _jsx("div", { className: "uxpcore_treeviewcompare__body", children: _jsx(TreeView, { ref: leftTreeRef, items: leftItems, loading: leftLoading, expandAllNodes: expandAllNodes, enableDrag: leftCanDrag, enableDrop: leftCanDrop, enableHierarchyChange: leftFeatures.enableInternalHierarchyChange || (isCrossTreeDrop && isDragAllowed), disableInternalDndContext: true, renderLabel: renderLeftLabel, renderDetails: renderLeftDetails, styles: treeStyles, showPath: showPath, maxDepth: maxDepth, externalDragState: getExternalDragState(Tree.Left), idPrefixForComparison: 'left', enableSearch: leftFeatures.enableSearch, onSearch: query => handleSearch(Tree.Left, query), enableAdd: leftFeatures.enableAdd, addButtonLabel: addButtonLabel, onAdd: parent => handleAdd(Tree.Left, parent), enableEdit: leftFeatures.enableEdit, onEdit: node => handleEdit(Tree.Left, node), enableDelete: leftFeatures.enableDelete, onDelete: id => handleDelete(Tree.Left, id), multiSelect: leftFeatures.multiSelect, selected: leftSelected, onSelect: (selected) => handleSelect(Tree.Left, selected), showActionButtonOnlyOnHover: showActionButtonOnlyOnHover, actionButtonsPosition: actionButtonsPosition, customActions: customActions }) })] }), _jsxs("div", { className: "uxpcore_treeviewcompare__panel uxpcore_treeviewcompare__panel--right", "data-tree-side": "right", children: [showHeaders && (_jsx("div", { className: "uxpcore_treeviewcompare__header", children: rightLoading ? (_jsx(SkeletonLoader, { width: '8rem', height: '1rem' })) : (rightTitle || 'Right Tree') })), _jsx("div", { className: "uxpcore_treeviewcompare__body", children: _jsx(TreeView, { ref: rightTreeRef, items: rightItems, loading: rightLoading, expandAllNodes: expandAllNodes, enableDrag: rightCanDrag, enableDrop: rightCanDrop, enableHierarchyChange: rightFeatures.enableInternalHierarchyChange || (isCrossTreeDrop && isDragAllowed), disableInternalDndContext: true, renderLabel: renderRightLabel, renderDetails: renderRightDetails, styles: treeStyles, showPath: showPath, maxDepth: maxDepth, externalDragState: getExternalDragState(Tree.Right), idPrefixForComparison: 'right', enableSearch: rightFeatures.enableSearch, onSearch: query => handleSearch(Tree.Right, query), enableAdd: rightFeatures.enableAdd, addButtonLabel: addButtonLabel, onAdd: parent => handleAdd(Tree.Right, parent), enableEdit: rightFeatures.enableEdit, onEdit: node => handleEdit(Tree.Right, node), enableDelete: rightFeatures.enableDelete, onDelete: id => handleDelete(Tree.Right, id), multiSelect: rightFeatures.multiSelect, selected: rightSelected, onSelect: (selected) => handleSelect(Tree.Right, selected), showActionButtonOnlyOnHover: showActionButtonOnlyOnHover, actionButtonsPosition: actionButtonsPosition, customActions: rightCustomActions }) })] }), _jsx(DragOverlay, { children: (dragState === null || dragState === void 0 ? void 0 : dragState.sourceNode) ? (_jsx("div", { className: "uxpcore_treeviewcompare__drag-overlay", children: _jsx("div", { className: "uxpcore_treeviewcompare__drag-overlay-content", children: _jsx("span", { className: "uxpcore_treeviewcompare__drag-overlay-label", children: (_a = dragState === null || dragState === void 0 ? void 0 : dragState.sourceNode) === null || _a === void 0 ? void 0 : _a.label }) }) })) : null })] }) }));
});
export default TreeViewCompare;
